import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { FlexProps } from '@iheartjane/reefer';
import { Flex, Link, Typography } from '@iheartjane/reefer';
import truncate from 'lodash-es/truncate';
import { useCallback, useContext, useMemo } from 'react';
import { useLocation } from 'react-router';

import { StoreDetailsContext } from '@jane/business-admin/providers';
import type { CheckoutAgreementV2 } from '@jane/business-admin/types';
import {
  EventNames,
  ExternalLinkSourceIds,
  normalizePath,
  track,
} from '@jane/business-admin/util';
import { normalizeUrl } from '@jane/shared/util';

type AgreementDetailsProps = Pick<
  CheckoutAgreementV2,
  'external_link' | 'label'
> & {
  external_links?: CheckoutAgreementV2['external_links'];
  showHeading?: boolean;
  truncateLabelTo: number | null;
};

export const AgreementDetails = ({
  label,
  external_link,
  external_links = [],
  truncateLabelTo = null,
  showHeading = false,
  ...flexProps
}: AgreementDetailsProps & FlexProps) => {
  const { storeId } = useContext(StoreDetailsContext);
  const { pathname } = useLocation();
  const multipleLinksEnabled = useFlag(FLAGS.c1MultipleCheckoutAgreements);

  const truncatedLabel = useMemo(() => {
    if (!truncateLabelTo) return label;
    return truncate(label, {
      length: truncateLabelTo,
      separator: /\s/,
    });
  }, [label, truncateLabelTo]);

  const trackExternalLink = useCallback(
    (to_url: string) => {
      track({
        event: EventNames.OpenExternalLink,
        from_url: normalizePath(pathname, storeId),
        to_url,
        trigger_source_id: ExternalLinkSourceIds.CheckoutAgreementDetails,
      });
    },
    [pathname, storeId]
  );

  return (
    <Flex flexDirection="column" {...flexProps}>
      {showHeading && (
        <Typography variant="caps" color="grays-mid" mb={8}>
          Details
        </Typography>
      )}
      <Typography whiteSpace="pre-wrap">{truncatedLabel}</Typography>
      {multipleLinksEnabled && external_links.length > 0
        ? external_links.map((link, index) => (
            <Flex key={index} flexDirection="column" mt={12}>
              <Link
                onClick={() => trackExternalLink(link.url)}
                href={normalizeUrl(link.url)}
              >
                {link.label}
              </Link>
              {!truncateLabelTo && (
                <Typography
                  variant="body"
                  color="grays-mid"
                  overflowWrap="anywhere"
                >
                  {link.url}
                </Typography>
              )}
            </Flex>
          ))
        : external_link && (
            <Link
              onClick={() => trackExternalLink(external_link)}
              href={normalizeUrl(external_link)}
              mt={24}
            >
              View Agreement
            </Link>
          )}
    </Flex>
  );
};
